import React, { useMemo, useState } from 'react'
import * as THREE from 'three'

interface AnalysisLayerProps {
  width?: number
  height?: number
  depth?: number
  position?: [number, number, number]
  faceColor?: string    
  hoverColor?: string  
  edgeColor?: string     
}

const AnalysisLayer: React.FC<AnalysisLayerProps> = ({
  width = 3,
  height = 0.1, 
  depth = 2,
  position = [0, 0, 0],
  faceColor = '#808080', 
  hoverColor = '#ff0000', 
  edgeColor = '#ffffff'  
}) => {
  const [hovered, setHovered] = useState(false)

  const displayColor = hovered ? hoverColor : faceColor

  const geometry = useMemo(
    () => new THREE.BoxGeometry(width, height, depth),
    [width, height, depth]
  )

  const edgesGeometry = useMemo(() => {
    const edges = new THREE.EdgesGeometry(geometry)
    const posAttr = edges.getAttribute('position')
    const positions = posAttr.array as number[]
    const lineDistances: number[] = []
    for (let i = 0; i < positions.length; i += 6) {
      const x1 = positions[i]
      const y1 = positions[i + 1]
      const z1 = positions[i + 2]
      const x2 = positions[i + 3]
      const y2 = positions[i + 4]
      const z2 = positions[i + 5]
      const segmentLength = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2 + (z2 - z1) ** 2)
      lineDistances.push(0, segmentLength)
    }
    edges.setAttribute('lineDistance', new THREE.Float32BufferAttribute(lineDistances, 1))
    return edges
  }, [geometry])

  return (
    <group position={position}>
      <mesh
        geometry={geometry}
        onPointerOver={(e) => {
          e.stopPropagation()
          setHovered(true)
        }}
        onPointerOut={(e) => {
          e.stopPropagation()
          setHovered(false)
        }}
      >
        <meshBasicMaterial
          color={displayColor}
          polygonOffset
          polygonOffsetFactor={1}
          polygonOffsetUnits={1}
        />
      </mesh>
      <lineSegments geometry={edgesGeometry}>
        <lineDashedMaterial
          color={edgeColor}
          dashSize={0.1}
          gapSize={0.05}
          linewidth={1}
          depthTest={true}
          depthFunc={THREE.GreaterDepth}
        />
      </lineSegments>
      <lineSegments geometry={edgesGeometry}>
        <lineBasicMaterial color={edgeColor} linewidth={1} />
      </lineSegments>
    </group>
  )
}

const AnalysisProcessing: React.FC = () => {
  return (
    <>
      <ambientLight intensity={0.5} />
      <directionalLight intensity={1} position={[10, 10, 5]} />
      <group rotation={[-Math.PI / 8, 0, 0]}>
        <AnalysisLayer position={[0, 0, 0]} faceColor="#808080" hoverColor="#02577a" />
        <AnalysisLayer position={[0, 0.3, 0]} faceColor="#808080" hoverColor="#89d6fb" />
        <AnalysisLayer position={[0, 0.6, 0]} faceColor="#808080" hoverColor="#02a9f7" />
      </group>
    </>
  )
}

export default AnalysisProcessing
